import { createServerFn } from "@tanstack/react-start";
import { authMiddleware } from "#/modules/util.auth/middleware.auth";
import { z } from "zod/v4";
import {
  getClassesForStartLesson,
  getStudentDetail,
  getStudents,
} from "#/modules/school.students/api.students.server";
import { getKlasAvatars } from "#/modules/school.klassen/api.klassen.server";
import { apiFailure } from "#/modules/util.errors/api-failure.server";
import { toFieldErrors, translateApiError } from "#/modules/util.errors/translate";
import { toastErrorMiddleware } from "#/modules/util.errors/middleware.toast-errors";
import { toResult } from "#/modules/util.auth/api-errors";
import type { StudentsSearch } from "#/modules/school.students/schema.search";
import {
  addLeerlingNotitieSchema,
  createLeerlingFormSchema,
  createLeerlingSchema,
  updateAvatarSchema,
  updateLeerlingSchema,
} from "#/modules/school.students/schema.students";
import { client } from "#/modules/util.fetch-client/fetch-client.server";
import type { components } from "#/schema";
import { Result } from "better-result";
import { getLeerlingNotities } from "#/modules/leerling.notities/api.leerling-notities.server";
import { getLeerlingStatistics } from "#/modules/leerling.statistics/api.leerling-statistics.server";
import { getLeerlingSportdeelname } from "#/modules/sportdeelname/api.sportdeelname.server";
import { getDefaultDateRange } from "#/modules/student/schema.search.beweegresultaten";
import type { BeweegresultatenSearch } from "#/modules/student/schema.search.beweegresultaten";
import type { SociaalresultatenSearch } from "#/modules/student/schema.search.sociaalresultaten";
import type { NotitiesSearch } from "#/modules/student/schema.search.notities";
import { apiEnum } from "#/modules/util.helpers/zod-enum";
import { Leerjaar, LeerjaarCombo, NotitieType } from "#/schema.nswag";

/** Students overview list + klassen (for the filter and pagination totals). */
export const getStudentsOverview = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator((data: StudentsSearch) => data)
  .handler(async ({ data }) => {
    const klassenResult = await getClassesForStartLesson();
    if (Result.isError(klassenResult)) throw klassenResult.error;
    const klassen = klassenResult.value.data ?? [];

    const studentsResult = await getStudents(data);
    if (Result.isError(studentsResult)) throw studentsResult.error;
    const response = studentsResult.value;

    const totalPages = Math.ceil((response.count ?? 0) / data.pageSize);

    return {
      students: response.data ?? [],
      totalPages,
      totalCount: response?.count ?? 0,
      klassen,
    };
  });

/** Data for the add/edit student modal: all klassen + (when editing) the student detail. */
export const getStudentModalData = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(z.object({ leerlingId: z.string().optional() }))
  .handler(async ({ data }) => {
    const klassenResult = await getClassesForStartLesson();
    if (Result.isError(klassenResult)) throw klassenResult.error;
    const klassen = klassenResult.value.data ?? [];

    if (!data.leerlingId) {
      return { student: null, klassen };
    }

    const studentResult = await getStudentDetail(data.leerlingId);
    if (Result.isError(studentResult)) {
      throw studentResult.error;
    }

    return { student: studentResult.value?.leerling ?? null, klassen };
  });

/** Create a leerling (portal's `students.api.create` action → POST server fn). */
export const submitCreateStudent = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(createLeerlingFormSchema)
  .handler(async ({ data }) => {
    const apiData = createLeerlingSchema.safeParse({
      ...data,
      leerjaren: [data.leerjaar],
    });

    if (!apiData.success) {
      const flattened = z.flattenError(apiData.error);
      return {
        formError: flattened.formErrors[0],
        fieldErrors: flattened.fieldErrors,
      };
    }

    const body: components["schemas"]["CreateLeerlingCommand"] = {
      studentNumber: apiData.data.studentNumber,
      firstName: apiData.data.firstName,
      lastName: apiData.data.lastName,
      birthDate: new Date(apiData.data.birthDate).toISOString(),
      gender: apiData.data.gender,
      klasId: apiData.data.klasId,
      leerjaren: apiData.data.leerjaren,
      avatarNummer: 1,
    };

    const result = toResult(await client.POST("/school/leerlingen", { body }));

    if (Result.isError(result)) {
      const fieldErrors = toFieldErrors(result.error);
      return fieldErrors ? { fieldErrors } : { formError: translateApiError(result.error) };
    }
  });

/** Update a leerling (portal's `students.$id.api.update` action → POST server fn). */
export const submitUpdateStudent = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(updateLeerlingSchema)
  .handler(async ({ data }) => {
    const result = toResult(
      await client.PUT("/school/leerlingen", {
        body: data,
      }),
    );

    if (Result.isError(result)) {
      const fieldErrors = toFieldErrors(result.error);
      return fieldErrors ? { fieldErrors } : { formError: translateApiError(result.error) };
    }
  });

/** Delete a leerling (portal's `students.$id.api.delete` action → POST server fn). */
export const submitDeleteStudent = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator(z.object({ leerlingId: z.string() }))
  .handler(async ({ data }) => {
    const result = toResult(
      await client.DELETE("/school/leerlingen", {
        body: { leerlingId: data.leerlingId },
      }),
    );

    if (Result.isError(result)) {
      return apiFailure(result.error);
    }
  });

/** Student detail (layout header + edit modal): student + klassen. Portal's `students.$id` loader. */
export const getStudentDetailData = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(z.object({ leerlingId: z.string() }))
  .handler(async ({ data }) => {
    const [studentResult, klassenResult] = await Promise.all([
      getStudentDetail(data.leerlingId),
      getClassesForStartLesson(),
    ]);

    if (Result.isError(studentResult)) throw studentResult.error;
    if (Result.isError(klassenResult)) throw klassenResult.error;

    return {
      student: studentResult.value?.leerling,
      avatarNummer: studentResult.value?.leerling.avatarNummer,
      klassen: klassenResult.value.data ?? [],
    };
  });

/** Dashboard tab: statistieken/latest cards + leerlijn & sociaal-emotioneel averages. */
export const getStudentDashboard = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(z.object({ leerlingId: z.string(), leerjaar: apiEnum(Leerjaar).optional() }))
  .handler(async ({ data }) => {
    const leerjaarQuery = data.leerjaar ? { leerjaar: data.leerjaar } : undefined;

    const [dashboardResult, averagesResult, sociaalResult] = await Promise.all([
      client
        .GET("/school/leerlingen/detail/dashboard", {
          params: { query: { leerlingId: data.leerlingId } },
        })
        .then(toResult),
      client
        .GET("/school/leerlingen/leerlijn-averages", {
          params: { query: { leerlingId: data.leerlingId, ...leerjaarQuery } },
        })
        .then(toResult),
      client
        .GET("/school/leerlingen/sociaal-emotioneel-themadomein-averages", {
          params: { query: { leerlingId: data.leerlingId, ...leerjaarQuery } },
        })
        .then(toResult),
    ]);

    if (Result.isError(dashboardResult)) throw dashboardResult.error;
    if (Result.isError(averagesResult)) throw averagesResult.error;
    if (Result.isError(sociaalResult)) throw sociaalResult.error;

    return {
      dashboardData: dashboardResult.value,
      leerlijnAveragesData: averagesResult.value,
      sociaalEmotioneelThemaDomeinAveragesData: sociaalResult.value,
    };
  });

/** Statistics tab: time-travel snapshots (beweeg + sociaal-emotioneel) with both tree filters. */
export const getStudentStatistics = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(
    z.object({
      leerlingId: z.string(),
      selectedLeerlijnFilterIds: z.array(z.string()).default([]),
      selectedDomeinFilterIds: z.array(z.string()).default([]),
    }),
  )
  .handler(async ({ data }) => {
    const result = await getLeerlingStatistics({
      leerlingId: data.leerlingId,
      selectedLeerlijnFilterIds: data.selectedLeerlijnFilterIds,
      selectedDomeinFilterIds: data.selectedDomeinFilterIds,
    });

    if (Result.isError(result)) {
      throw result.error;
    }

    const statistics = result.value;

    return {
      snapshots: statistics?.snapshots ?? [],
      leerlijnFilter: statistics?.leerlijnFilter,
      domeinFilter: statistics?.domeinFilter,
      currentLeerjaar: statistics?.currentLeerjaar,
    };
  });

/** Notities tab: filtered + paginated notities list. */
export const getStudentNotities = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator((data: { leerlingId: string } & NotitiesSearch) => data)
  .handler(async ({ data }) => {
    const { leerlingId, page, pageSize, ...filters } = data;

    const result = await getLeerlingNotities({
      leerlingId,
      page,
      pageSize,
      ...filters,
    });

    if (Result.isError(result)) {
      throw result.error;
    }

    const response = result.value;
    const totalPages = Math.ceil((response?.count ?? 0) / pageSize);

    return {
      notities: response?.data ?? [],
      count: response?.count ?? 0,
      totalPages,
      leerlingNaam: response?.leerlingNaam ?? "",
    };
  });

/** Add a notitie on the leerling itself; failures are toasted by `toastErrorMiddleware`. */
export const submitAddLeerlingNotitie = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator(addLeerlingNotitieSchema)
  .handler(async ({ data }) => {
    const result = toResult(await client.POST("/school/leerlingen/notities", { body: data }));

    if (Result.isError(result)) {
      return apiFailure(result.error);
    }
  });

/**
 * Delete a single notitie from the notities tab (TASK 837).
 * Failures throw a translated Error; `toastErrorMiddleware` toasts it client-side,
 * so callers only handle the success path (and swallow the rethrow).
 */
export const submitDeleteNotitie = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator(
    z.object({
      id: z.string(),
      notitieType: apiEnum(NotitieType),
      leerlingId: z.string().optional(),
    }),
  )
  .handler(async ({ data }) => {
    const result = toResult(
      await client.DELETE("/school/leerlingen/notities", {
        body: {
          id: data.id,
          notitieType: data.notitieType,
          leerlingId: data.leerlingId,
        },
      }),
    );

    if (Result.isError(result)) {
      return apiFailure(result.error);
    }
  });

/** Beweegresultaten tab: beweegactiviteiten progress + leerlijn tree filter. */
export const getStudentBeweegResults = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator((data: { leerlingId: string } & BeweegresultatenSearch) => data)
  .handler(async ({ data }) => {
    const defaults = getDefaultDateRange();

    const result = toResult(
      await client.GET("/school/leerlingen/beweegactiviteiten-progress", {
        params: {
          query: {
            leerlingId: data.leerlingId,
            from: data.from || defaults.from,
            to: data.to || defaults.to,
            "filterSelection.selectedLeerlijnFilterIds": data.selectedLeerlijnFilterIds,
            includeWithProgress: data.includeWithProgress,
            includeWithoutProgress: data.includeWithoutProgress,
          },
        },
      }),
    );

    if (Result.isError(result)) {
      throw result.error;
    }

    return {
      beweegactiviteitProgressItems: result.value?.beweegactiviteitProgressItems ?? [],
      leerlijnFilter: result.value?.leerlijnFilter,
    };
  });

/** Beweegresultaten historie drawer. */
export const getStudentBeweegHistorie = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(
    z.object({
      leerlingId: z.string(),
      beweegactiviteitenReeksId: z.string(),
      leerjaarCombo: apiEnum(LeerjaarCombo),
    }),
  )
  .handler(async ({ data }) => {
    const result = toResult(
      await client.GET("/school/leerlingen/beweegresultaten-historie", {
        params: {
          query: {
            leerlingId: data.leerlingId,
            beweegactiviteitenReeksId: data.beweegactiviteitenReeksId,
            leerjaarCombo: data.leerjaarCombo,
          },
        },
      }),
    );

    if (Result.isError(result)) {
      throw result.error;
    }

    return { historie: result.value };
  });

/** Historie drawer: "leeg" one historic beweegresultaat (the row stays, only its score goes). */
export const submitClearBeweegresultaat = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator(z.object({ leerlingId: z.string(), beweegresultaatId: z.string() }))
  .handler(async ({ data }) => {
    const result = toResult(
      await client.POST("/school/leerlingen/beweegresultaten-historie/leegmaken", {
        body: data,
      }),
    );

    if (Result.isError(result)) {
      return apiFailure(result.error);
    }
  });

/** Sociaal-emotionele resultaten tab: themas progress + domein tree filter. */
export const getStudentSociaalResults = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator((data: { leerlingId: string } & SociaalresultatenSearch) => data)
  .handler(async ({ data }) => {
    const defaults = getDefaultDateRange();

    const result = toResult(
      await client.GET("/school/leerlingen/sociaal-emotioneel-themas-progress", {
        params: {
          query: {
            leerlingId: data.leerlingId,
            from: data.from || defaults.from,
            to: data.to || defaults.to,
            "filterSelection.selectedDomeinFilterIds": data.selectedDomeinFilterIds,
            includeWithProgress: data.includeWithProgress,
            includeWithoutProgress: data.includeWithoutProgress,
          },
        },
      }),
    );

    if (Result.isError(result)) {
      throw result.error;
    }

    return {
      sociaalEmotioneelThemaProgressItems: result.value?.sociaalEmotioneelThemaProgressItems ?? [],
      domeinFilter: result.value?.domeinFilter,
    };
  });

/** Sociaal-emotionele resultaten historie drawer. */
export const getStudentSociaalHistorie = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(
    z.object({
      leerlingId: z.string(),
      sociaalEmotioneelThemasReeksId: z.string(),
      leerjaarCombo: apiEnum(LeerjaarCombo),
    }),
  )
  .handler(async ({ data }) => {
    const result = toResult(
      await client.GET("/school/leerlingen/sociaal-emotioneel-thema-resultaten-historie", {
        params: {
          query: {
            leerlingId: data.leerlingId,
            sociaalEmotioneelThemasReeksId: data.sociaalEmotioneelThemasReeksId,
            leerjaarCombo: data.leerjaarCombo,
          },
        },
      }),
    );

    if (Result.isError(result)) {
      throw result.error;
    }

    return { historie: result.value };
  });

/** Historie drawer: "leeg" one historic sociaal-emotioneel resultaat (the row stays). */
export const submitClearSociaalResultaat = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator(z.object({ leerlingId: z.string(), sociaalEmotioneelResultaatId: z.string() }))
  .handler(async ({ data }) => {
    const result = toResult(
      await client.POST(
        "/school/leerlingen/sociaal-emotioneel-thema-resultaten-historie/leegmaken",
        {
          body: data,
        },
      ),
    );

    if (Result.isError(result)) {
      return apiFailure(result.error);
    }
  });

/** Sport-participation tab: leerling sportdeelname (sporten/verenigingen/mrt/etc.). */
export const getStudentSportParticipation = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(z.object({ leerlingId: z.string() }))
  .handler(async ({ data }) => {
    const result = await getLeerlingSportdeelname(data.leerlingId);

    if (Result.isError(result)) {
      throw result.error;
    }

    return { data: result.value };
  });

/** Avatar picker: per avatar of the klas, whether it's in use and by which leerling (TASK 859). */
export const getKlasAvatarOverview = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(z.object({ klasId: z.string() }))
  .handler(async ({ data }) => {
    const result = await getKlasAvatars(data.klasId);

    if (Result.isError(result)) throw result.error;

    return { avatars: result.value?.avatars ?? [] };
  });

/** Avatar picker: update the leerling's avatarNummer (portal's `students.$id.api.avatar`). */
export const submitUpdateStudentAvatar = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator(updateAvatarSchema)
  .handler(async ({ data }) => {
    const result = toResult(
      await client.POST("/school/leerlingen/detail/avatar", {
        body: {
          leerlingId: data.leerlingId,
          avatarNummer: data.avatarNummer,
        },
      }),
    );

    if (Result.isError(result)) {
      throw new Error("Failed to update avatar");
    }
  });
